import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import ImageUpload from "../../components/ImageUpload";
import RichTextEditor from "../../components/RichTextEditor";
import { Save, Settings as SettingsIcon, Type, Home, Star, Megaphone, Users, FileText, MessageCircle, UserRound, Handshake, Shield, Bell } from "lucide-react";
import { DEFAULT_SEO_PAGES, SEO_PAGE_LABELS, getSeoPages, normalizeSlug } from "../../utils/seoPages";

const SECTIONS = [
  { id: "brand", label: "Marque & contact", icon: Type },
  { id: "hero", label: "Hero (page d'accueil)", icon: Home },
  { id: "founder", label: "Présentation fondateur", icon: UserRound },
  { id: "stats", label: "Stats overlay", icon: Star },
  { id: "features", label: "3 features", icon: SettingsIcon },
  { id: "catalog", label: "Section catalogue", icon: FileText },
  { id: "affiliate", label: "Bandeau parrainage", icon: Megaphone },
  { id: "affiliatePage", label: "Page parrainage publique", icon: Handshake },
  { id: "community", label: "CTA communauté", icon: MessageCircle },
  { id: "legal", label: "Pages légales", icon: Shield },
  { id: "seo", label: "SEO & Tracking", icon: SettingsIcon },
  { id: "booking", label: "RDV apprenants", icon: MessageCircle },
  { id: "abandonedCart", label: "Paniers abandonnés", icon: Megaphone },
  { id: "systemAlerts", label: "Alertes système", icon: Bell },
  { id: "footer", label: "Footer", icon: Users },
];

const FIELDS = {
  brand: [
    ["site_name", "Nom du site", "input"],
    ["tagline", "Slogan (sous le logo)", "input"],
    ["contact_email", "Email de contact", "input"],
    ["whatsapp_number", "N° WhatsApp (international, sans espaces ni +)", "input"],
  ],
  hero: [
    ["hero_image", "Image hero page d'accueil", "image"],
    ["hero_image_position", "Cadrage vertical de l'image hero", "select", ["center top", "center 20%", "center 30%", "center center", "center 60%", "center 70%", "center bottom"]],
    ["hero_badge", "Badge en haut du hero", "input"],
    ["hero_title_line1", "Titre — ligne 1", "input"],
    ["hero_title_line2", "Titre — ligne 2 (mise en valeur verte)", "input"],
    ["hero_title_line3", "Titre — ligne 3 (avec barre orange)", "input"],
    ["hero_subtitle", "Sous-titre / accroche", "textarea"],
    ["hero_cta_primary", "Bouton principal (vert)", "input"],
    ["hero_cta_secondary", "Bouton secondaire (orange)", "input"],
    ["hero_social_proof", "Preuve sociale (sous les avatars)", "input"],
  ],
  founder: [
    ["founder_enabled", "Afficher le bloc présentation", "checkbox"],
    ["founder_image", "Photo de présentation", "image"],
    ["founder_image_width", "Taille de la photo", "select", ["w-56", "w-64", "w-72", "w-80", "w-96", "w-full"]],
    ["founder_title", "Titre", "input"],
    ["founder_title_tag", "Balise du titre", "select", ["h1", "h2", "h3", "h4", "h5", "h6", "p"]],
    ["founder_name", "Nom", "input"],
    ["founder_name_tag", "Balise du nom", "select", ["h1", "h2", "h3", "h4", "h5", "h6", "p"]],
    ["founder_punchline", "Punchline", "input"],
    ["founder_punchline_tag", "Balise de la punchline", "select", ["h1", "h2", "h3", "h4", "h5", "h6", "p"]],
    ["founder_body_1", "Texte — paragraphe 1", "textarea"],
    ["founder_body_1_tag", "Balise du paragraphe 1", "select", ["h1", "h2", "h3", "h4", "h5", "h6", "p"]],
    ["founder_body_2", "Texte — paragraphe 2", "textarea"],
    ["founder_body_2_tag", "Balise du paragraphe 2", "select", ["h1", "h2", "h3", "h4", "h5", "h6", "p"]],
    ["founder_cta_text", "Texte du bouton", "input"],
    ["founder_cta_url", "Lien du bouton", "input"],
    ["founder_facebook_url", "Lien Facebook", "input"],
    ["founder_instagram_url", "Lien Instagram", "input"],
    ["founder_youtube_url", "Lien YouTube", "input"],
    ["founder_linkedin_url", "Lien LinkedIn", "input"],
    ["founder_tiktok_url", "Lien TikTok", "input"],
  ],
  stats: [
    ["stat_community_label", "Carte 1 — label", "input"],
    ["stat_community_value", "Carte 1 — valeur", "input"],
    ["stat_assignments_label", "Carte 2 — label", "input"],
    ["stat_assignments_value", "Carte 2 — valeur", "input"],
  ],
  features: [
    ["feature1_title", "Feature 1 — titre", "input"],
    ["feature1_text", "Feature 1 — description", "textarea"],
    ["feature2_title", "Feature 2 — titre", "input"],
    ["feature2_text", "Feature 2 — description", "textarea"],
    ["feature3_title", "Feature 3 — titre", "input"],
    ["feature3_text", "Feature 3 — description", "textarea"],
  ],
  catalog: [
    ["catalog_label", "Petit label au-dessus", "input"],
    ["catalog_title_line1", "Titre — ligne 1", "input"],
    ["catalog_title_line2", "Titre — ligne 2 (mot vert)", "input"],
  ],
  affiliate: [
    ["affiliate_label", "Petit label", "input"],
    ["affiliate_title_line1", "Titre — ligne 1", "input"],
    ["affiliate_title_line2", "Titre — ligne 2 (mot vert)", "input"],
    ["affiliate_subtitle", "Sous-titre", "textarea"],
    ["affiliate_cta", "Texte du bouton orange", "input"],
  ],
  affiliatePage: [
    ["affiliate_page_title", "Titre de la page /affiliate", "input"],
    ["affiliate_page_subtitle", "Sous-titre de la page /affiliate", "textarea"],
    ["affiliate_page_description", "Description / texte complémentaire", "textarea"],
    ["affiliate_page_cta", "Texte du bouton de connexion", "input"],
    ["affiliate_page_form_cta", "Texte du bouton du formulaire", "input"],
  ],
  community: [
    ["community_title", "Titre", "input"],
    ["community_subtitle", "Sous-titre", "textarea"],
    ["community_cta", "Texte du bouton", "input"],
  ],
  legal: [
    ["legal_mentions", "Mentions légales", "textarea"],
    ["privacy_policy", "Politique de confidentialité", "textarea"],
    ["cgv_content", "Contrat & Conditions Générales de Vente", "textarea"],
  ],
  seo: [
    ["google_analytics_id", "ID Google Analytics 4 (ex : G-XXXXXXXXXX)", "input"],
    ["google_search_console_verification", "Code de vérification Google Search Console", "input"],
    ["sitemap_enabled", "Activer le sitemap.xml automatique", "checkbox"],
    ["robots_txt", "Contenu robots.txt", "textarea"],
  ],
  booking: [
    ["zcal_embed_enabled", "Afficher le calendrier de RDV dans l'espace apprenant", "checkbox"],
    ["zcal_booking_url", "Lien Zcal de prise de rendez-vous", "input"],
  ],
  abandonedCart: [
    ["abandoned_cart_enabled", "Activer la relance automatique panier abandonné", "checkbox"],
    ["abandoned_cart_delay_hours", "Délai avant relance (heures)", "input"],
  ],
  systemAlerts: [
    ["system_alert_emails_enabled", "Activer les emails d’alerte Logs & Santé", "checkbox"],
    ["system_alert_emails", "Email(s) destinataire(s) des alertes système", "input"],
  ],
  footer: [
    ["footer_description", "Description sous le logo", "textarea"],
    ["footer_contact_text", "Texte de la zone Contact", "textarea"],
    ["footer_copyright", "Mention copyright (après l'année)", "input"],
  ],
};

export default function AdminSettings() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [active, setActive] = useState("brand");
  const [savingMsg, setSavingMsg] = useState("");

  useEffect(() => { setForm(settings); }, [settings]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateSeoPage = (path, field, value) => {
    setForm((f) => {
      const pages = getSeoPages(f);
      const current = pages[path] || DEFAULT_SEO_PAGES[path] || {};
      return {
        ...f,
        seo_pages: {
          ...pages,
          [path]: {
            ...current,
            [field]: field === "slug" ? normalizeSlug(value) : value,
          },
        },
      };
    });
  };

  const save = async () => {
    setSavingMsg("Sauvegarde...");
    try {
      await api.patch("/settings", form);
      await refresh();
      setSavingMsg("Enregistré ✓ — Le site utilise les nouveaux contenus.");
      setTimeout(() => setSavingMsg(""), 4000);
    } catch (e) {
      setSavingMsg("❌ " + (e.response?.data?.detail || "Erreur"));
    }
  };

  const fields = FIELDS[active] || [];
  const seoPages = getSeoPages(form);

  const renderField = ([key, label, kind, options]) => {
    if (kind === "image") {
      const isPersistentSettingsImage = key === "founder_image" || key === "hero_image";
      return (
        <ImageUpload
          value={form[key] || ""}
          onChange={(url) => update(key, url)}
          label={label}
          type="image"
          storage={isPersistentSettingsImage ? "dataUrl" : "upload"}
        />
      );
    }

    if (kind === "checkbox") {
      return (
        <label className="flex items-center gap-3 text-sm font-semibold text-gray-700">
          <input
            type="checkbox"
            checked={form[key] !== false}
            onChange={(e) => update(key, e.target.checked)}
            data-testid={`settings-input-${key}`}
          />
          {label}
        </label>
      );
    }

    if (kind === "select") {
      const optionLabels = {
        "center top": "Visage très haut",
        "center 20%": "Visage haut",
        "center 30%": "Un peu haut",
        "center center": "Centre",
        "center 60%": "Un peu bas",
        "center 70%": "Bas",
        "center bottom": "Très bas",
      };

      return (
        <div>
          <label className="label mb-1 block">{label}</label>
          <select
            className="input"
            value={form[key] || options?.[0] || ""}
            onChange={(e) => update(key, e.target.value)}
            data-testid={`settings-input-${key}`}
          >
            {(options || []).map((option) => <option key={option} value={option}>{optionLabels[option] || option}</option>)}
          </select>

          {key === "hero_image_position" && form.hero_image && (
            <div className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Aperçu du cadrage</p>
              <div className="relative mx-auto aspect-[4/5] max-w-[220px] overflow-hidden rounded-2xl bg-white shadow-sm">
                <img
                  src={form.hero_image}
                  alt="Aperçu image hero"
                  className="h-full w-full object-cover"
                  style={{ objectPosition: form.hero_image_position || "center center" }}
                />
              </div>
            </div>
          )}

          <p className="text-xs text-gray-400 mt-1 font-mono">{key}</p>
        </div>
      );
    }

    return (
      <div>
        <label className="label mb-1 block">{label}</label>
        {kind === "textarea" ? (
          <RichTextEditor
            value={form[key] || ""}
            onChange={(value) => update(key, value)}
            placeholder={label}
            minHeight={150}
          />
        ) : (
          <input
            className="input"
            value={form[key] || ""}
            onChange={(e) => update(key, e.target.value)}
            data-testid={`settings-input-${key}`}
          />
        )}
        <p className="text-xs text-gray-400 mt-1 font-mono">{key}</p>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-12" data-testid="admin-settings-page">
      <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
        <div>
          <p className="label">Admin</p>
          <h1 className="font-heading text-3xl font-black flex items-center gap-2"><SettingsIcon className="w-7 h-7 text-brand-green" /> Textes du site</h1>
          <p className="text-sm text-gray-600 mt-1">Édite ici les textes, images et liens des sections principales du site.</p>
        </div>
        <button onClick={save} className="btn-primary" data-testid="settings-save-btn"><Save className="w-4 h-4" /> Enregistrer</button>
      </div>

      {savingMsg && <p className="card p-3 text-sm text-emerald-700 bg-emerald-50 mb-4">{savingMsg}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside className="card p-3 self-start">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition ${active === s.id ? "bg-brand-green text-white font-semibold" : "hover:bg-emerald-50"}`}
              data-testid={`settings-tab-${s.id}`}
            >
              <s.icon className="w-4 h-4" /> {s.label}
            </button>
          ))}
        </aside>

        <main className="card p-6 space-y-4">
          <h2 className="font-heading text-xl font-bold mb-2">{SECTIONS.find((s) => s.id === active)?.label}</h2>
          {active === "founder" && (
            <p className="text-sm text-gray-600 bg-emerald-50 border border-emerald-100 rounded-xl p-3">
              Cette section pilote le bloc “Qui suis-je ?” de la page d'accueil : photo, tailles, textes, balises H1 à H6, bouton et réseaux sociaux.
            </p>
          )}
          {active === "seo" && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 bg-emerald-50 border border-emerald-100 rounded-xl p-3">
                Ajoute ici Google Analytics, la vérification Search Console, le robots.txt et le sitemap automatique. Le robots.txt accepte la variable {"{{ site_url }}"}.
              </p>
              <div className="rounded-2xl border border-emerald-100 bg-white p-4 space-y-4">
                <div>
                  <h3 className="font-heading text-lg font-bold">Pages publiques du sitemap</h3>
                  <p className="text-sm text-gray-500">Modifie ici l’URL, le titre Google et la meta description des pages publiques. L’accueil garde toujours l’URL <code>/</code>.</p>
                </div>
                {Object.entries(DEFAULT_SEO_PAGES).map(([path, defaults]) => {
                  const page = seoPages[path] || defaults;
                  const publicUrl = path === "/" ? "/" : `/${normalizeSlug(page.slug || defaults.slug)}`;
                  return (
                    <div key={path} className="rounded-xl border border-gray-100 bg-emerald-50/40 p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <p className="font-semibold text-sm">{SEO_PAGE_LABELS[path] || path}</p>
                          <p className="text-xs text-gray-500">Chemin actuel : <code>{publicUrl}</code> · Chemin historique conservé : <code>{path}</code></p>
                        </div>
                      </div>
                      {path !== "/" && (
                        <label className="block">
                          <span className="label mb-1 block">URL / slug</span>
                          <input
                            className="input font-mono"
                            value={page.slug || defaults.slug || ""}
                            onChange={(e) => updateSeoPage(path, "slug", e.target.value)}
                            placeholder={defaults.slug}
                            data-testid={`seo-page-slug-${path}`}
                          />
                        </label>
                      )}
                      <label className="block">
                        <span className="label mb-1 block">Titre SEO ({(page.title || "").length}/60)</span>
                        <input
                          className="input"
                          value={page.title || ""}
                          onChange={(e) => updateSeoPage(path, "title", e.target.value)}
                          placeholder={defaults.title}
                          data-testid={`seo-page-title-${path}`}
                        />
                      </label>
                      <label className="block">
                        <span className="label mb-1 block">Description SEO ({(page.description || "").length}/160)</span>
                        <textarea
                          className="input"
                          rows={2}
                          value={page.description || ""}
                          onChange={(e) => updateSeoPage(path, "description", e.target.value)}
                          placeholder={defaults.description}
                          data-testid={`seo-page-description-${path}`}
                        />
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {active === "abandonedCart" && (
            <p className="text-sm text-gray-600 bg-amber-50 border border-amber-100 rounded-xl p-3">
              La relance utilise le modèle email “Relance panier abandonné 12h” dans Admin → Emails. Le serveur vérifie les commandes en attente toutes les heures.
            </p>
          )}
          {active === "systemAlerts" && (
            <p className="text-sm text-gray-600 bg-emerald-50 border border-emerald-100 rounded-xl p-3">
              Configure ici les destinataires des alertes envoyées quand un événement warning, error ou critical apparaît dans Logs & Santé. Tu peux mettre plusieurs emails séparés par des virgules. Un seul email est envoyé tant qu’il reste des événements à traiter.
            </p>
          )}
          {fields.map((field) => <div key={field[0]}>{renderField(field)}</div>)}
          <div className="pt-4 border-t border-emerald-100">
            <button onClick={save} className="btn-primary" data-testid="settings-save-btn-bottom"><Save className="w-4 h-4" /> Enregistrer cette section</button>
          </div>
        </main>
      </div>
    </div>
  );
}
